/**
 * NATIVE — the bridge to the app shell, and nothing else.
 *
 * shop.supercanic.com is served three ways: an ordinary browser, an
 * installed PWA, and inside the Capacitor shell in `shopdesk-app`, which
 * points a WebView at this same live site rather than shipping a copy of
 * it. Only the third has a camera plugin or a push token, and it announces
 * itself by injecting `window.Capacitor` into this origin before any of
 * our script runs — Capacitor 7 uses WebViewCompat.addDocumentStartJavaScript
 * against server.url and every allowNavigation entry, so the global is
 * genuinely here on the live site and not only on a bundled page.
 *
 * Everything native goes through this file so no screen reads that global
 * directly again. Before 2026-09-20 three pages each tested it their own
 * way — index.html to skip the service worker, manuals.html to pick a pay
 * link, driver.html to mark a pass digital — and driver.html had already
 * had to learn the hard half: `isNativePlatform` is a FUNCTION on a current
 * shell and simply absent on an older injected bridge, where the presence
 * of the object is itself the answer.
 *
 * THIS FILE MUST NOT IMPORT A @capacitor PACKAGE. The site does not depend
 * on one and must not start: the plugins live in the shell, and this same
 * bundle is served to desktop browsers with no bridge at all. Reaching the
 * plugins through the injected `Plugins` object is what lets one build work
 * in both places. An import would also fail the moment the shell and the
 * site disagreed on a version, which they will, because they release on
 * different days through different stores.
 */

import { gpsOfPluginExif, injectGps, readGps } from "./photogps.ts";

/* The shape we rely on, not the shape Capacitor has. Written narrow on
   purpose: anything this file does not name, it cannot accidentally use. */
interface CameraPhoto {
  webPath?: string;
  format?: string;
  /* What the plugin read off the shot. Android: raw EXIF strings; iOS: the
     picker's metadata. Either may be missing, and often is. */
  exif?: unknown;
}
interface CameraPlugin {
  getPhoto(o: Record<string, unknown>): Promise<CameraPhoto>;
  pickImages(o: Record<string, unknown>): Promise<{ photos?: CameraPhoto[] }>;
}
interface PermissionState {
  receive?: string;
}
interface PushPlugin {
  checkPermissions(): Promise<PermissionState>;
  requestPermissions(): Promise<PermissionState>;
  register(): Promise<void>;
  addListener(event: string, cb: (data: unknown) => void): Promise<unknown>;
}
interface Bridge {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
  Plugins?: Record<string, unknown>;
}

const bridge = (): Bridge | undefined =>
  (globalThis as unknown as { Capacitor?: Bridge }).Capacitor;

/**
 * Inside the app shell?
 *
 * The fallback to `true` is deliberate and is driver.html's lesson: if the
 * object exists at all, something injected it, and the only thing that
 * injects it is a shell. Guessing "web" there would silently take the
 * camera away from the people who have one.
 */
export function isNative(): boolean {
  const c = bridge();
  if (!c) return false;
  return typeof c.isNativePlatform === "function" ? c.isNativePlatform() : true;
}

export function nativePlatform(): "ios" | "android" | "web" {
  const c = bridge();
  const p = typeof c?.getPlatform === "function" ? c.getPlatform() : "";
  return p === "ios" || p === "android" ? p : "web";
}

/**
 * A plugin, or undefined when the shell does not carry it.
 *
 * Undefined is a normal answer, not a failure. A shell released in August
 * does not have a plugin added in September, and the phones in the bays
 * update when their owners feel like it. Every caller here falls back to
 * the web path rather than telling somebody at a car that the app is
 * broken.
 */
function plugin<T>(name: string): T | undefined {
  const p = bridge()?.Plugins;
  if (!p) return undefined;
  const found = p[name];
  return found ? (found as T) : undefined;
}

export const hasCamera = (): boolean => isNative() && !!plugin("Camera");

/* The shell's own manifest deliberately does NOT declare CAMERA. Capacitor's
   WebView file picker only offers camera capture while that permission is
   undeclared (BridgeWebChromeClient.isMediaCaptureSupported), so declaring it
   would break the ordinary <input type=file capture> path that every shell
   before this one relied on. The Camera plugin does not need the declaration
   either: it fires ACTION_IMAGE_CAPTURE and the camera app holds the
   permission. Do not "fix" the manifest to match this file. */

/** A stable-ish filename. The server renames it; this is for the error. */
const stamp = (ext: string): string =>
  `camera-${new Date().toISOString().replace(/[:.]/g, "-")}.${ext}`;

async function toFile(p: CameraPhoto): Promise<File | null> {
  if (!p.webPath) return null;
  // webPath is a capacitor://localhost/_capacitor_file_... URL that only the
  // WebView can read, which is why this fetch happens here and the rest of
  // the app only ever sees a File.
  const res = await fetch(p.webPath);
  const blob = await res.blob();
  const fmt = (p.format ?? "jpeg").toLowerCase();
  const type = blob.type
    || (fmt === "png" ? "image/png" : fmt === "webp" ? "image/webp" : "image/jpeg");
  const ext = type === "image/png" ? "png" : type === "image/webp" ? "webp" : "jpg";

  // Anthony, 2026-10-27: photos keep the spot they were taken. When the
  // plugin re-encodes (correctOrientation does) the file can come back
  // without its GPS while the plugin still reports it in `exif`. Put it
  // back here, so shrink() finds it in the file like any other photo.
  let body: Blob = blob;
  if (type === "image/jpeg" && !(await readGps(blob))) {
    body = await injectGps(blob, gpsOfPluginExif(p.exif));
  }
  return new File([body], stamp(ext), { type });
}

/**
 * One photo from the back camera, or null if the person backed out.
 *
 * `correctOrientation` matters more than it sounds: a photo taken sideways
 * arrives with its rotation in EXIF, shrink() re-encodes through a canvas,
 * and a canvas does not read EXIF — so an uncorrected photo lands in the
 * job upside down. The plugin applies the rotation to the pixels before we
 * ever see it.
 *
 * This also quietly ends the HEIC problem. addPhoto() has to refuse HEIC
 * with a note telling somebody to go and change a setting in Settings >
 * Camera, because a browser cannot decode it. getPhoto hands back JPEG on
 * both platforms whatever the phone saves in, so on the app that message
 * can no longer be reached.
 */
export async function captureNativePhoto(): Promise<File | null> {
  const cam = plugin<CameraPlugin>("Camera");
  if (!cam) return null;
  const shot = await cam.getPhoto({
    resultType: "uri",
    source: "CAMERA",
    quality: 85,
    correctOrientation: true,
    // The shot is for a job, not for the owner's camera roll. Saving it
    // would fill a technician's phone with pictures of other people's cars.
    saveToGallery: false,
    allowEditing: false,
  });
  return await toFile(shot);
}

/**
 * Photos from the gallery. Alaina's case from the web build: she had
 * already taken them and had no way to put them on a job.
 */
export async function pickNativePhotos(limit = 8): Promise<File[]> {
  const cam = plugin<CameraPlugin>("Camera");
  if (!cam) return [];
  const got = await cam.pickImages({ quality: 85, limit });
  const out: File[] = [];
  for (const p of got.photos ?? []) {
    const f = await toFile(p);
    if (f) out.push(f);
  }
  return out;
}

/* ─── PUSH ──────────────────────────────────────────────────────────── */

export const hasPush = (): boolean => isNative() && !!plugin("PushNotifications");

/**
 * Ask for a push token. Resolves to null for every ordinary reason — no
 * shell, no plugin, permission refused, or no delivery credential on the
 * project yet — and rejects for none of them.
 *
 * That last one is today's case and the reason this is written to fail
 * quietly. Android needs a google-services.json in the shell and iOS needs
 * an APNs key on a paid Apple account; neither exists on 2026-09-20. Until
 * they do, `register()` resolves and the registration event never fires, so
 * this returns null after its timeout and the app carries on. The moment
 * the credentials land, the same code starts returning tokens with no site
 * release, because the shell points at the live site.
 */
export async function requestPushToken(timeoutMs = 10000): Promise<string | null> {
  const push = plugin<PushPlugin>("PushNotifications");
  if (!push) return null;

  let perm = await push.checkPermissions();
  if (perm.receive === "prompt" || perm.receive === "prompt-with-rationale") {
    perm = await push.requestPermissions();
  }
  // 'denied' is a settled answer, not an error. Somebody said no, and
  // asking again on the next boot is how an app gets deleted.
  if (perm.receive !== "granted") return null;

  return await new Promise<string | null>((resolve) => {
    let done = false;
    const finish = (v: string | null): void => {
      if (done) return;
      done = true;
      resolve(v);
    };
    // A timeout rather than a hang. Registration that never calls back is
    // the NORMAL state while FCM and APNs are unconfigured, and a promise
    // that never settles would wedge whatever awaited it at boot.
    const timer = setTimeout(() => finish(null), timeoutMs);
    void push.addListener("registration", (data) => {
      clearTimeout(timer);
      const token = (data as { value?: string } | null)?.value;
      finish(typeof token === "string" && token ? token : null);
    });
    void push.addListener("registrationError", () => {
      clearTimeout(timer);
      finish(null);
    });
    void push.register().catch(() => {
      clearTimeout(timer);
      finish(null);
    });
  });
}
